import { XQWebCrypto } from '@xqmsg/jssdk-core';
import { getEncryptionKey, exchangeEncryptionKey } from '@/api/encryption.api';

/**
 * XQ Encryption implementation using XQWebCrypto
 * @param data - The data to encrypt (object will be JSON stringified)
 * @param key - The encryption key
 * @returns Promise<string> - The encrypted data as base64 string
 */
export const XQEncryptData = async (
  data: any,
  key: string,
  shouldPrefixKey: boolean = true,
): Promise<string> => {
  try {
    // Convert data to string if it's an object
    const dataString = typeof data === 'string' ? data : JSON.stringify(data);

    // Use OTP scheme prefix for data encryption (similar to file encryption)
    let prefixedKey = key;
    if (shouldPrefixKey) {
      const dataPrefix = `.${XQWebCrypto.otp.fileScheme}`;
      prefixedKey = `${dataPrefix}${key}`;
    }

    // Use OTP encrypt - header is optional and typed as null
    const encrypted = await XQWebCrypto.otp.encrypt(
      dataString,
      prefixedKey,
      false, // raw = false to get base64 string
      // header parameter is optional and typed as null, so we omit it
    );

    // The result can be a string (base64) or array (Promise<string | any[]>)
    if (typeof encrypted === 'string') {
      return encrypted;
    } else if (Array.isArray(encrypted)) {
      // Convert array to base64 string
      const uint8Array = new Uint8Array(encrypted as number[]);
      const base64 = btoa(String.fromCharCode(...uint8Array));
      return base64;
    } else {
      return encrypted;
    }
  } catch (exception) {
    console.error('Failed to encrypt data:', exception);
    throw new Error(`Encryption failed: ${exception}`);
  }
};

/**
 * XQ Decryption implementation using XQWebCrypto
 * @param encryptedData - The encrypted data (base64 string or array)
 * @param key - The encryption key
 * @returns Promise<any> - The decrypted data (parsed JSON if valid JSON, otherwise string)
 */
export const XQDecryptData = async (
  encryptedData: string,
  key: string,
  shouldPrefixKey: boolean = true,
): Promise<any> => {
  try {
    // Use OTP scheme prefix for data decryption (similar to encryption)
    let prefixedKey = key;
    if (shouldPrefixKey) {
      const dataPrefix = `.${XQWebCrypto.otp.fileScheme}`;
      prefixedKey = `${dataPrefix}${key}`;
    }

    // Use OTP decrypt
    const decrypted = await XQWebCrypto.otp.decrypt(
      encryptedData,
      prefixedKey,
      false, // raw = false to get string
    );
    return decrypted;
  } catch (exception) {
    throw new Error(`Decryption failed: ${exception}`);
  }
};

/**
 * Parses XQ auto-encrypted format to extract locator token and ciphertext
 * - Bytes 0-3 (4 bytes): locator_token_size + version
 * - Bytes 4-46 (43 bytes): locator_bytes (the locator token)
 * - Byte 47 (1 byte): scheme byte
 * - Bytes 48+: ciphertext
 * @param encryptedDataBase64 - Base64 encoded encrypted data
 * @returns Object containing locator token and ciphertext (base64 encoded)
 */
export const extractXQAutoEncryptedFormat = (
  encryptedDataBase64: string,
): { locatorToken: string; ciphertextBase64: string } => {
  const HEADER_SIZE = 48; // Always 48 bytes for auto format: 4 + 43 + 1

  const binaryString = atob(encryptedDataBase64);
  const view = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    view[i] = binaryString.charCodeAt(i);
  }

  if (view.length < HEADER_SIZE) {
    throw new Error(
      `Message too short: ${view.length} bytes, need at least ${HEADER_SIZE} for auto format header`,
    );
  }

  const locatorBytes = view.slice(4, 47);
  const locatorToken = new TextDecoder('utf-8', { fatal: true }).decode(
    locatorBytes,
  );

  const ciphertextBytes = view.slice(HEADER_SIZE);
  // Use spread for typical payload sizes; for very large buffers consider a loop to avoid stack limits
  const ciphertextBinary = String.fromCharCode(...ciphertextBytes);
  const ciphertextBase64 = btoa(ciphertextBinary);

  return {
    locatorToken,
    ciphertextBase64,
  };
};

/**
 * Decrypts an XQ auto-encrypted value that contains a locator token
 * @param encryptedDataBase64 - Base64 encoded encrypted data with XQ auto format header
 * @returns Promise with decrypted data
 */
export const decryptXQValueWithLocator = async (
  encryptedDataBase64: string,
): Promise<any> => {
  const { locatorToken, ciphertextBase64 } =
    extractXQAutoEncryptedFormat(encryptedDataBase64);

  const exchangeKeyResponse = await exchangeEncryptionKey(locatorToken);
  if (!exchangeKeyResponse.data?.key) {
    throw new Error('Failed to exchange locator token for decryption key');
  }

  return XQDecryptData(
    ciphertextBase64,
    exchangeKeyResponse.data.key,
    false,
  );
};

/**
 * Decrypts an XQ encrypted value, automatically detecting the format
 * (auto format with locator token, or OTP format)
 * @param encryptedDataBase64 - Base64 encoded encrypted data
 * @returns Promise with decrypted data
 */
export const decryptXQValueAuto = async (
  encryptedDataBase64: string,
): Promise<any> => {
  try {
    return await decryptXQValueWithLocator(encryptedDataBase64);
  } catch (error) {
    // Only log in development to avoid noisy fallback in production
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        'Failed to parse as XQ auto format, trying OTP format:',
        error,
      );
    }
  }

  const encryptionKeyResponse = await getEncryptionKey();
  if (!encryptionKeyResponse.data?.key) {
    throw new Error('Failed to get encryption key');
  }

  return XQDecryptData(
    encryptedDataBase64,
    encryptionKeyResponse.data.key,
    true,
  );
};
